'use client';

import { TextField, TextPassage } from '@petsmart-ui/sparky';
import { type ChangeEvent, forwardRef } from 'react';

import { clsx } from '@libs/common-next';

import { useStreetAddressInput } from './use-street-address-input';

type StreetAddressInputProps = {
    value: string;
    onAddressSelect: (address: { street: string; city: string; state: string; zip: string }) => void;
    isError?: boolean;
    fieldNote?: string;
    name?: string;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
};

export const StreetAddressInput = forwardRef<HTMLInputElement, StreetAddressInputProps>(
    ({ value, onAddressSelect, isError, fieldNote, name, onChange, onBlur }, ref) => {
        const { inputValue, suggestions, containerRef, handleInputChange, handleAddressSelect } = useStreetAddressInput(
            {
                value,
                onAddressSelect,
                onChange,
            }
        );

        return (
            <div className="sdd-form-input__street-container" ref={containerRef}>
                <TextField
                    ref={ref}
                    name={name}
                    data-cs-mask
                    data-testid="sdd-street"
                    className="sdd-form-input"
                    label="Street address"
                    isError={isError}
                    type="text"
                    fieldNote={fieldNote}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={onBlur}
                />
                <div
                    data-testid="sdd-street-dropdown"
                    className={clsx(
                        'sdd-form-input__street-dropdown',
                        suggestions.length === 0 && 'sdd-form-input__street-dropdown--hidden'
                    )}
                >
                    {suggestions.map(suggestion => (
                        <TextPassage
                            data-cs-mask
                            data-testid="sdd-street-suggestion"
                            key={suggestion.global_address_key}
                            onClick={() => handleAddressSelect(suggestion.global_address_key)}
                            className="sdd-form-input__street-dropdown__item"
                        >
                            {suggestion.text}
                        </TextPassage>
                    ))}
                </div>
            </div>
        );
    }
);

StreetAddressInput.displayName = 'StreetAddressInput';
